import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalculatorIcon,
  ChartIcon,
  Divider,
  DocumentIcon,
  GKButton,
  GKSearchSelect,
  GKSideModal,
  GKTextField,
  Grid2,
  NoData,
  PercentageIcon,
  RightIcon,
  Show,
  Typography,
} from "components";
import { EquityBasketListInterface } from "interfaces/EquityBasket";
import {
  OrderSimulationForm,
  OrderSimulationTemplate,
} from "interfaces/OrderSimulation";
import { isEmpty } from "lodash";
import { useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SETTING_TAB } from "store/slices/appSlice";
import { handleCapitalize } from "utils/common";
import { GKModalProps } from "utils/commonInterface";
import CompanyEditTable from "./CompanyEditTable";
import CompanyModel from "./CompanyModel";
import GenerateOrderSheetModal from "./GenerateOrderSheetModal";
import GenerateTradeCompany from "./GenerateTradeCompany";
import {
  AllocationByCompany,
  AllocationByModel,
  ExitBy,
  RebalanceByCustom,
  RebalanceByModel,
} from "./GenerateTradeHeaderCard";
import GenerateTradeTable from "./GenerateTradeTable";
import PortfolioEditTable from "./PortfolioEditTable";
import PortfolioSelectionTable from "./PortfolioSelectionTable";
import StockWiseRebalanceTable from "./StockWiseRebalanceTable";
import {
  AllocationByTooltipContent,
  AllocationTypeTooltipContent,
  ExitReferenceValueCompanyTooltip,
  ModelReferenceWeightage,
  ReferenceValueCompanyTooltip,
  ReferenceValueTooltip,
  WeightageDistributionMethod,
} from "./TooltipContent";
import { ReturnBasketTitle, useOrderSimulation } from "./utils";

interface Props {
  modelData: EquityBasketListInterface;
  orderSimulationTemplate: OrderSimulationTemplate | undefined;
}

interface FlattenedOverrideRow {
  grid_code: string;
  id: number;
  quantity: number;
}

interface Plan {
  gridCode: string;
  quantity: number;
}

const OrderSimulation = (props: GKModalProps & Props) => {
  const { open, setOpen, modelData, orderSimulationTemplate } = props;

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    ALLOCATION_BY,
    ALLOCATION_TYPE,
    REFERENCE_TYPE,
    TRANSACTION_TYPE,
    WEIGHTAGE_DISTRIBUTION_METHOD,
    allocationVersionList,
    allocationVersionListLoading,
    modelPortfolioList,
    modelPortfolioFetching,
    userData,
    form,
    generateTrade,
    ALLOCATION_BY_EXIT,
    returnExitByTitle,
    companies,
    isLoading,
    watchAllocationType,
    watchAllocationBy,
    watchRightVisibleTable,
    REFERENCE_TYPE_FOR_HEADER_CARD,
    CUSTOMIZED_PARAMETER,
    matchObjectsStrict,
  } = useOrderSimulation({ modelData });

  const [openCompanyModel, setOpenCompanyModel] = useState(false);
  const [openRationalModal, setOpenRationalModal] = useState(false);

  const onSubmit = (values: OrderSimulationForm) => {
    const isRebalanceByCompany =
      values.allocation?.allocType === "rebalance" &&
      values.allocation?.allocationBy === "company";

    const customQTY: FlattenedOverrideRow[] =
      values.generateTradeResponse?.flatMap((portfolio) =>
        (portfolio.plan || []).map((plan: Plan) => ({
          grid_code: plan?.gridCode,
          id: portfolio?.id,
          quantity: plan.quantity,
        })),
      ) || [];

    const baseAllocation = {
      ...values.allocation,
      useCurrentAllocation:
        values.allocation?.useCurrentAllocation === "current",
      stockWise: false,
    };

    if (isRebalanceByCompany) {
      baseAllocation.allocationBy = "version";
      baseAllocation.stockWise = true;

      if (watchRightVisibleTable === "GENERATE_TRADE_TABLE") {
        baseAllocation.customQuantities = customQTY;
        form.setValue("generateResponsePayload", {
          ...values.allocation,
          customQuantities: customQTY,
        });
      }
    }

    const isMatch = matchObjectsStrict(
      form.getValues("generateResponsePayload"),
      { ...values.allocation, customQuantities: customQTY },
    );

    if (isMatch) {
      form.setValue("extraThings.rightVisibleTable", "GENERATE_TRADE_TABLE");
    } else {
      form.setValue("generateResponsePayload", {
        ...values.allocation,
        customQuantities: customQTY,
      });

      generateTrade(baseAllocation);
    }
  };

  return (
    <>
      <GKSideModal
        open={open}
        setOpen={setOpen}
        modalTitle={<ReturnBasketTitle basketName={modelData?.name} />}
        width={"100%"}
      >
        <FormProvider {...form}>
          <form
            style={{ height: "100%", width: "100%" }}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <Grid2 container spacing={2} height={"100%"}>
              <Show.When
                isTrue={watchRightVisibleTable !== "GENERATE_TRADE_TABLE"}
              >
                <Grid2
                  size={3}
                  container
                  spacing={4}
                  alignContent={"flex-start"}
                  sx={{
                    opacity:
                      [
                        "EDIT_PORTFOLIO",
                        "EDIT_COMPANY",
                        "BUY_COMPANY",
                        "SELL_COMPANY",
                      ].includes(watchRightVisibleTable) && 0.33,
                    transition: "all 0.3s",
                    "&:hover": { opacity: 1 },
                  }}
                  maxHeight={"calc(100vh - 160px)"}
                  overflow={"auto"}
                >
                  <Grid2 size={12}>
                    <Controller
                      control={form.control}
                      name="allocation.allocType"
                      render={({ field }) => (
                        <GKSearchSelect
                          {...field}
                          disableClearable
                          inputLabel="Allocation Type"
                          options={ALLOCATION_TYPE}
                          onChange={(event, val) => {
                            const selectedAllocType = val?.value || "";

                            const allocKey = selectedAllocType as keyof Pick<
                              OrderSimulationTemplate,
                              "exit" | "rebalance" | "allocation"
                            >;

                            form.reset({
                              ...form.formState.defaultValues,
                              allocation: {
                                allocType: selectedAllocType,
                                allocationBy: orderSimulationTemplate
                                  ? orderSimulationTemplate[allocKey]
                                      ?.allocationBy
                                  : "",
                                useCurrentAllocation: orderSimulationTemplate
                                  ? orderSimulationTemplate[allocKey]
                                      ?.useCurrentAllocation
                                  : null,
                                rebalanceUsing: orderSimulationTemplate
                                  ? orderSimulationTemplate[allocKey]
                                      ?.rebalanceUsing
                                  : "",
                                weightageDistributionMethod:
                                  orderSimulationTemplate
                                    ? orderSimulationTemplate[allocKey]
                                        ?.weightageDistributionMethod
                                    : "",
                                portfolios: [],
                                companies: [],
                                allocVersion: null,
                                exitWeightage: "",
                                frozenCompanies: [],
                                holdCompanies: [],
                                buyCompanies: [],
                                sellCompanies: [],
                                exitParameter: orderSimulationTemplate
                                  ? orderSimulationTemplate[allocKey]
                                      ?.exitParameter
                                  : "",
                                buyParameter: orderSimulationTemplate
                                  ? orderSimulationTemplate[allocKey]
                                      ?.buyParameter
                                  : "",
                              },
                            });

                            field.onChange(selectedAllocType);
                          }}
                          value={
                            ALLOCATION_TYPE.find(
                              (type) => type.value === field.value,
                            )?.name || []
                          }
                          getOptionLabel={(option) => option.name || option}
                          tooltipModal={{
                            isTrue: true,
                            tooltipModelLabel: "Allocation Type",
                            tooltipModalContent: (
                              <AllocationTypeTooltipContent />
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid2>
                  <Show.When isTrue={!!watchAllocationType}>
                    <Grid2 size={12} container spacing={4}>
                      <Grid2
                        size={12}
                        container
                        bgcolor={" #F8F8F8"}
                        borderRadius={1}
                        p={"16px 12px"}
                      >
                        <Grid2 size={12}>
                          <Controller
                            control={form.control}
                            name="allocation.allocationBy"
                            render={({ field }) => (
                              <GKSearchSelect
                                {...field}
                                disableClearable
                                inputLabel={
                                  watchAllocationType === "allocation"
                                    ? "Allocation By"
                                    : watchAllocationType === "rebalance"
                                      ? "Rebalance By"
                                      : "Exit By"
                                }
                                options={
                                  watchAllocationType === "exit"
                                    ? ALLOCATION_BY_EXIT
                                    : ALLOCATION_BY.filter((dataItem) =>
                                        watchAllocationType !== "rebalance"
                                          ? dataItem.value !== "customized"
                                          : dataItem,
                                      )
                                }
                                onChange={(event, val) => {
                                  const selectedAllocBy = val?.value || "";

                                  form.reset({
                                    ...form.formState.defaultValues,
                                    allocation: {
                                      allocType: form.getValues(
                                        "allocation.allocType",
                                      ),
                                      allocationBy: selectedAllocBy,
                                      useCurrentAllocation: null,
                                      rebalanceUsing: "",
                                      weightageDistributionMethod: "",
                                      portfolios: [],
                                      companies: [],
                                      allocVersion: null,
                                      exitWeightage: "",
                                      frozenCompanies: [],
                                      holdCompanies: [],
                                      buyCompanies: [],
                                      sellCompanies: [],
                                      exitParameter: "",
                                      buyParameter: "",
                                    },
                                  });

                                  field.onChange(selectedAllocBy);
                                }}
                                value={
                                  (watchAllocationType === "exit"
                                    ? ALLOCATION_BY_EXIT
                                    : ALLOCATION_BY
                                  ).find((type) => type.value === field.value)
                                    ?.name || []
                                }
                                getOptionLabel={(option) =>
                                  option.name || option
                                }
                                tooltipModal={{
                                  isTrue: true,
                                  tooltipModelLabel:
                                    watchAllocationType === "allocation"
                                      ? "Allocation By"
                                      : watchAllocationType === "rebalance"
                                        ? "Rebalance By"
                                        : "Exit By",
                                  tooltipModalContent: (
                                    <AllocationByTooltipContent
                                      allocationType={watchAllocationType}
                                    />
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid2>
                        <Show.When
                          isTrue={
                            (watchAllocationType === "allocation" &&
                              watchAllocationBy === "version") ||
                            (watchAllocationType === "rebalance" &&
                              watchAllocationBy !== "customized")
                          }
                        >
                          <Grid2 size={12}>
                            <Controller
                              control={form.control}
                              name="allocation.useCurrentAllocation"
                              render={({ field }) => {
                                return (
                                  <GKSearchSelect
                                    {...field}
                                    disableClearable
                                    inputLabel="Model Reference Weightage"
                                    options={TRANSACTION_TYPE}
                                    onChange={(event, val) => {
                                      field.onChange(val?.value);
                                    }}
                                    value={
                                      TRANSACTION_TYPE?.find(
                                        (item) => item.value === field.value,
                                      )?.name
                                    }
                                    getOptionLabel={(option) =>
                                      option.name || option
                                    }
                                    tooltipModal={{
                                      isTrue: true,
                                      tooltipModelLabel:
                                        "Model Reference Weightage",
                                      tooltipModalContent: (
                                        <ModelReferenceWeightage />
                                      ),
                                    }}
                                  />
                                );
                              }}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            watchAllocationType === "allocation" ||
                            (watchAllocationType === "exit" &&
                              watchAllocationBy === "companyWeightage") ||
                            watchAllocationType === "rebalance"
                          }
                        >
                          <Grid2 size={12}>
                            <Controller
                              control={form.control}
                              name="allocation.rebalanceUsing"
                              render={({ field }) => (
                                <GKSearchSelect
                                  {...field}
                                  disableClearable
                                  inputLabel={`${handleCapitalize(watchAllocationType)} Reference Value`}
                                  options={REFERENCE_TYPE.filter(
                                    (dataItem) =>
                                      dataItem?.allocationType?.includes(
                                        watchAllocationType,
                                      ) &&
                                      dataItem[
                                        watchAllocationType as keyof typeof dataItem
                                      ]?.includes(watchAllocationBy),
                                  ).map((dataItem) => ({
                                    ...dataItem,
                                    label: dataItem.name,
                                    value: dataItem.value,
                                  }))}
                                  onChange={(event, val) =>
                                    field.onChange(val?.value)
                                  }
                                  value={
                                    REFERENCE_TYPE.filter(
                                      (dataItem) =>
                                        dataItem?.allocationType?.includes(
                                          watchAllocationType,
                                        ) &&
                                        dataItem[
                                          watchAllocationType as keyof typeof dataItem
                                        ]?.includes(watchAllocationBy),
                                    )
                                      .map((dataItem) => ({
                                        ...dataItem,
                                        label: dataItem.name,
                                        value: dataItem.value,
                                      }))
                                      .find(
                                        (option) =>
                                          option.value === field.value,
                                      ) || null
                                  }
                                  getOptionLabel={(option) =>
                                    typeof option === "string"
                                      ? option
                                      : option.label
                                  }
                                  tooltipModal={{
                                    isTrue: true,
                                    tooltipModelLabel: `${handleCapitalize(watchAllocationType)} Reference Value`,
                                    tooltipModalContent:
                                      watchAllocationType === "allocation" &&
                                      watchAllocationBy === "company" ? (
                                        <ReferenceValueCompanyTooltip />
                                      ) : watchAllocationType === "exit" &&
                                        watchAllocationBy ===
                                          "companyWeightage" ? (
                                        <ExitReferenceValueCompanyTooltip />
                                      ) : (
                                        <ReferenceValueTooltip
                                          allocationType={watchAllocationType}
                                        />
                                      ),
                                  }}
                                />
                              )}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            (watchAllocationType === "allocation" &&
                              watchAllocationBy === "version") ||
                            (watchAllocationType === "rebalance" &&
                              watchAllocationBy !== "customized")
                          }
                        >
                          <Grid2 size={12}>
                            <Controller
                              control={form.control}
                              name="allocation.weightageDistributionMethod"
                              render={({ field }) => (
                                <GKSearchSelect
                                  {...field}
                                  disableClearable
                                  inputLabel="Weightage Distribution Method"
                                  options={WEIGHTAGE_DISTRIBUTION_METHOD}
                                  onChange={(event, val) => {
                                    field.onChange(val?.value);
                                  }}
                                  value={
                                    WEIGHTAGE_DISTRIBUTION_METHOD.find(
                                      (type) => type.value === field.value,
                                    )?.name
                                  }
                                  getOptionLabel={(option) =>
                                    option.name || option
                                  }
                                  tooltipModal={{
                                    isTrue: true,
                                    tooltipModelLabel:
                                      "Weightage Distribution Method",
                                    tooltipModalContent: (
                                      <WeightageDistributionMethod />
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            watchAllocationType === "rebalance" &&
                            watchAllocationBy === "company"
                          }
                        >
                          <Grid2 size={12} mr={4}>
                            <Controller
                              control={form.control}
                              name="allocation.companies"
                              render={({ field }) => (
                                <GKSearchSelect
                                  {...field}
                                  disableClearable={false}
                                  inputLabel="Company Name"
                                  options={companies || []}
                                  value={
                                    companies?.filter((option: any) =>
                                      field.value
                                        .map((dataItem) => dataItem?.value)
                                        .includes(option?.value),
                                    )[0]?.name
                                  }
                                  onChange={(e, val) => {
                                    field.onChange(
                                      val ? [{ ...val, weightage: 0 }] : [],
                                    );
                                  }}
                                  getOptionLabel={(option) =>
                                    option.name || option
                                  }
                                />
                              )}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            (["allocation", "exit"].includes(
                              watchAllocationType,
                            ) &&
                              ["version"].includes(watchAllocationBy)) ||
                            (watchAllocationType === "rebalance" &&
                              watchAllocationBy !== "customized")
                          }
                        >
                          <Grid2 size={12} mr={4}>
                            <Controller
                              control={form.control}
                              name="allocation.allocVersion"
                              render={({ field }) => {
                                const selectedOption =
                                  allocationVersionList?.data?.data?.find(
                                    (dataItem: { id: number }) =>
                                      dataItem.id === field.value,
                                  ) || null;

                                return (
                                  <GKSearchSelect
                                    loading={allocationVersionListLoading}
                                    inputLabel="Select Model"
                                    value={selectedOption}
                                    onChange={(e, val) => {
                                      field.onChange(val ? val?.id : null);
                                    }}
                                    disableClearable={false}
                                    options={
                                      allocationVersionList?.data?.data || []
                                    }
                                    getOptionLabel={(option) =>
                                      option?.version || option
                                    }
                                  />
                                );
                              }}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            ["exit"].includes(watchAllocationType) &&
                            ["version"].includes(watchAllocationBy)
                          }
                        >
                          <Grid2 size={12} mr={4}>
                            <Controller
                              control={form.control}
                              name="allocation.exitWeightage"
                              render={({ field }) => (
                                <GKTextField
                                  {...field}
                                  size="medium"
                                  placeholder="Weightage"
                                  type="number"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    field.onChange(
                                      val === "" ? "" : Number(val),
                                    );
                                  }}
                                  slotProps={{
                                    input: { endAdornment: <PercentageIcon /> },
                                  }}
                                />
                              )}
                            />
                          </Grid2>
                        </Show.When>
                        <Show.When
                          isTrue={
                            watchAllocationType === "rebalance" &&
                            watchAllocationBy === "customized"
                          }
                        >
                          <Grid2 size={12} mr={4}>
                            <Controller
                              control={form.control}
                              name="allocation.exitParameter"
                              render={({ field }) => {
                                const selectedOption =
                                  CUSTOMIZED_PARAMETER.find(
                                    (dataItem: { value: string }) =>
                                      dataItem.value === field.value,
                                  ) || null;

                                return (
                                  <GKSearchSelect
                                    loading={allocationVersionListLoading}
                                    inputLabel="Exit Parameter"
                                    value={selectedOption}
                                    onChange={(e, val) => {
                                      field.onChange(val ? val?.value : null);
                                    }}
                                    disableClearable={false}
                                    options={
                                      CUSTOMIZED_PARAMETER.filter(
                                        (dataItem) =>
                                          dataItem?.value !==
                                          "sellAmountAllocation",
                                      ) || []
                                    }
                                    getOptionLabel={(option) =>
                                      option?.version || option
                                    }
                                  />
                                );
                              }}
                            />
                          </Grid2>
                        </Show.When>

                        <Show.When
                          isTrue={
                            watchAllocationType === "rebalance" &&
                            watchAllocationBy === "customized"
                          }
                        >
                          <Grid2 size={12} mr={4}>
                            <Controller
                              control={form.control}
                              name="allocation.buyParameter"
                              render={({ field }) => {
                                const selectedOption =
                                  CUSTOMIZED_PARAMETER.find(
                                    (dataItem: { value: string }) =>
                                      dataItem.value === field.value,
                                  ) || null;

                                return (
                                  <GKSearchSelect
                                    loading={allocationVersionListLoading}
                                    inputLabel="Buy Parameter"
                                    value={selectedOption}
                                    onChange={(e, val) => {
                                      field.onChange(val ? val?.value : null);
                                    }}
                                    disableClearable={false}
                                    options={
                                      CUSTOMIZED_PARAMETER.filter(
                                        (dataItem) =>
                                          dataItem?.value !== "companyAbsolute",
                                      ) || []
                                    }
                                    getOptionLabel={(option) =>
                                      option?.version || option
                                    }
                                  />
                                );
                              }}
                            />
                          </Grid2>
                        </Show.When>
                      </Grid2>
                      <Show.When
                        isTrue={
                          ![
                            "EDIT_PORTFOLIO",
                            "EDIT_COMPANY",
                            "GENERATE_TRADE_TABLE",
                          ].includes(watchRightVisibleTable)
                        }
                      >
                        <Grid2 size={12}>
                          <Controller
                            control={form.control}
                            name="extraThings.rightVisibleTable"
                            render={({ field }) => (
                              <GKButton
                                fullWidth
                                variant="contained"
                                startIcon={<ChartIcon />}
                                onClick={() =>
                                  field.onChange("SELECT_PORTFOLIO")
                                }
                              >
                                Select Portfolio
                              </GKButton>
                            )}
                          />
                        </Grid2>
                      </Show.When>
                    </Grid2>
                  </Show.When>
                  <Show.When isTrue={isEmpty(orderSimulationTemplate)}>
                    <Grid2
                      container
                      size={12}
                      spacing={1}
                      borderRadius={1}
                      border={"1px solid #D5DEE5"}
                      p={1.5}
                    >
                      <Grid2
                        size={12}
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"flex-start"}
                        gap={1.5}
                      >
                        <Typography
                          fontSize={12}
                          fontWeight={600}
                          color=" #333"
                        >
                          Set up a default template for faster Allocation
                          Settings in the future.
                        </Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography
                          fontSize={12}
                          fontWeight={500}
                          color=" #697586"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            dispatch(
                              SETTING_TAB({
                                name: "Order & Execution",
                                type: "EXECUTION_SHEET",
                              }),
                            );
                            navigate("/app/user-profile/settings");
                          }}
                        >
                          Click here to Go to Settings
                        </Typography>
                      </Grid2>
                    </Grid2>
                  </Show.When>
                </Grid2>
                <Grid2 size={0.2}>
                  <Divider orientation="vertical" />
                </Grid2>
              </Show.When>
              <Grid2
                size={
                  watchRightVisibleTable === "GENERATE_TRADE_TABLE" ? 12 : 8.8
                }
                container
                spacing={1}
                alignContent={"flex-start"}
                minHeight={"calc(100vh - 160px)"}
                maxHeight={"calc(100vh - 160px)"}
                overflow={"auto"}
              >
                <Show.When
                  isTrue={
                    ![
                      "GENERATE_TRADE_TABLE",
                      "SELECT_PORTFOLIO",
                      "EDIT_PORTFOLIO",
                      "EDIT_COMPANY",
                      "SELL_COMPANY",
                      "BUY_COMPANY",
                    ].includes(watchRightVisibleTable)
                  }
                >
                  <Grid2 size={12}>
                    <NoData
                      text="No Rebalance, Allocation or Exit done yet."
                      subText="Please fill up the form to proceed forward"
                    />
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={watchRightVisibleTable === "GENERATE_TRADE_TABLE"}
                >
                  {watchAllocationType === "allocation" &&
                    watchAllocationBy === "version" && (
                      <AllocationByModel
                        referenceType={REFERENCE_TYPE_FOR_HEADER_CARD}
                        weightageDistributionMethod={
                          WEIGHTAGE_DISTRIBUTION_METHOD
                        }
                        allocationVersion={allocationVersionList?.data?.data}
                      />
                    )}

                  {watchAllocationType === "allocation" &&
                    watchAllocationBy === "company" && (
                      <AllocationByCompany
                        referenceType={REFERENCE_TYPE_FOR_HEADER_CARD}
                      />
                    )}

                  {watchAllocationType === "rebalance" &&
                    watchAllocationBy !== "customized" && (
                      <RebalanceByModel
                        rebalanceBy={watchAllocationBy}
                        referenceType={REFERENCE_TYPE_FOR_HEADER_CARD}
                        weightageDistributionMethod={
                          WEIGHTAGE_DISTRIBUTION_METHOD
                        }
                        allocationVersion={allocationVersionList?.data?.data}
                      />
                    )}
                  {watchAllocationType === "rebalance" &&
                    watchAllocationBy === "customized" && (
                      <RebalanceByCustom
                        referenceType={REFERENCE_TYPE_FOR_HEADER_CARD}
                        customParameter={CUSTOMIZED_PARAMETER}
                      />
                    )}

                  {watchAllocationType === "exit" &&
                    [
                      "portfolio",
                      "version",
                      "company",
                      "companyWeightage",
                    ].includes(watchAllocationBy) && (
                      <ExitBy
                        customProp={returnExitByTitle(watchAllocationBy)}
                      />
                    )}
                </Show.When>
                <Show.When
                  isTrue={watchRightVisibleTable === "SELECT_PORTFOLIO"}
                >
                  <Grid2 size={12}>
                    <PortfolioSelectionTable
                      modelPortfolioList={modelPortfolioList?.data.data ?? []}
                      modelPortfolioFetching={modelPortfolioFetching}
                      watchAllocationType={watchAllocationType}
                    />
                  </Grid2>
                </Show.When>
                <Show.When isTrue={watchRightVisibleTable === "EDIT_PORTFOLIO"}>
                  <Grid2 size={12}>
                    <PortfolioEditTable
                      userData={userData}
                      watchAllocationType={watchAllocationType}
                    />
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={["EDIT_COMPANY", "SELL_COMPANY"]?.includes(
                    watchRightVisibleTable,
                  )}
                >
                  <Grid2 size={12}>
                    <CompanyEditTable
                      watchAllocationType={watchAllocationType}
                      isRebalanceCustomSell={
                        watchAllocationType === "rebalance" &&
                        watchAllocationBy === "customized" &&
                        watchRightVisibleTable === "SELL_COMPANY"
                      }
                    />
                  </Grid2>
                </Show.When>

                <Show.When
                  isTrue={["BUY_COMPANY"]?.includes(watchRightVisibleTable)}
                >
                  <Grid2 size={12}>
                    <CompanyEditTable
                      watchAllocationType={watchAllocationType}
                      isRebalanceCustomBuy={
                        watchAllocationType === "rebalance" &&
                        watchAllocationBy === "customized" &&
                        watchRightVisibleTable === "BUY_COMPANY"
                      }
                    />
                  </Grid2>
                </Show.When>

                {/* ALLOCATION-TYPE = 'ALLOCATION', ALLOCATION-BY= 'MODEL' */}
                <Show.When
                  isTrue={
                    watchRightVisibleTable === "GENERATE_TRADE_TABLE" &&
                    ((watchAllocationType === "allocation" &&
                      watchAllocationBy === "version") ||
                      (watchAllocationType === "exit" &&
                        ["portfolio", "version"].includes(watchAllocationBy)) ||
                      (watchAllocationType === "rebalance" &&
                        ["version", "customized"].includes(watchAllocationBy)))
                  }
                >
                  <Grid2 size={12}>
                    <GenerateTradeTable
                      watchAllocationType={watchAllocationType}
                      watchAllocationBy={watchAllocationBy}
                    />
                  </Grid2>
                </Show.When>

                {/* ALLOCATION-TYPE = 'REBALANCE', ALLOCATION-BY= 'COMPANY' */}
                <Show.When
                  isTrue={
                    watchRightVisibleTable === "GENERATE_TRADE_TABLE" &&
                    watchAllocationType === "rebalance" &&
                    watchAllocationBy === "company"
                  }
                >
                  <Grid2 size={12}>
                    <StockWiseRebalanceTable />
                  </Grid2>
                </Show.When>

                {/* ALLOCATION-TYPE = 'ALLOCATION', ALLOCATION-BY= 'COMPANY' */}
                <Show.When
                  isTrue={
                    watchRightVisibleTable === "GENERATE_TRADE_TABLE" &&
                    ((watchAllocationType === "allocation" &&
                      watchAllocationBy === "company") ||
                      (watchAllocationType === "exit" &&
                        ["company", "companyWeightage"].includes(
                          watchAllocationBy,
                        )))
                  }
                >
                  <Grid2 size={12}>
                    <GenerateTradeCompany />
                  </Grid2>
                </Show.When>
              </Grid2>

              <Show.When
                isTrue={
                  ["SELECT_PORTFOLIO", "EDIT_PORTFOLIO"].includes(
                    watchRightVisibleTable,
                  ) &&
                  ((watchAllocationType === "allocation" &&
                    watchAllocationBy === "version") ||
                    (watchAllocationType === "exit" &&
                      watchAllocationBy === "portfolio") ||
                    (watchAllocationType === "rebalance" &&
                      watchAllocationBy === "version"))
                }
              >
                <Grid2 size={12} container>
                  <Grid2 size={3.2} />
                  <Grid2
                    size={8.8}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={
                      watchRightVisibleTable === "EDIT_PORTFOLIO"
                        ? "space-between"
                        : "flex-end"
                    }
                  >
                    <GKButton
                      variant={
                        watchRightVisibleTable === "EDIT_PORTFOLIO"
                          ? "outlined"
                          : "contained"
                      }
                      startIcon={
                        watchRightVisibleTable === "EDIT_PORTFOLIO" && (
                          <ArrowLeftIcon />
                        )
                      }
                      endIcon={
                        watchRightVisibleTable === "SELECT_PORTFOLIO" && (
                          <RightIcon />
                        )
                      }
                      color={
                        watchRightVisibleTable === "EDIT_PORTFOLIO"
                          ? "dark"
                          : "primary"
                      }
                      disabled={
                        form.watch("allocation.portfolios")?.length === 0
                      }
                      onClick={() => {
                        form.setValue(
                          "extraThings.rightVisibleTable",
                          watchRightVisibleTable === "EDIT_PORTFOLIO"
                            ? "SELECT_PORTFOLIO"
                            : "EDIT_PORTFOLIO",
                        );
                      }}
                    >
                      {watchRightVisibleTable === "SELECT_PORTFOLIO"
                        ? "Next"
                        : "Back to Portfolios"}
                    </GKButton>

                    {watchRightVisibleTable === "EDIT_PORTFOLIO" && (
                      <GKButton
                        variant={"contained"}
                        startIcon={<DocumentIcon color="var(--white)" />}
                        endIcon={<ArrowRightIcon />}
                        type="submit"
                        loading={isLoading}
                      >
                        Generate Trades
                      </GKButton>
                    )}

                    {watchRightVisibleTable === "GENERATE_TRADE_TABLE" && (
                      <GKButton
                        variant={"outlined"}
                        color={"dark"}
                        startIcon={<CalculatorIcon />}
                        type="submit"
                        loading={isLoading}
                      >
                        Re-Calculate
                      </GKButton>
                    )}
                  </Grid2>
                </Grid2>
              </Show.When>

              {/* ALLOCATION-TYPE = 'ALLOCATION', ALLOCATION-BY= 'COMPANY' */}
              <Show.When
                isTrue={
                  (watchAllocationType === "allocation" &&
                    watchAllocationBy === "company") ||
                  (watchAllocationType === "rebalance" &&
                    watchAllocationBy === "customized") ||
                  (watchAllocationType === "exit" &&
                    ["company", "companyWeightage"].includes(
                      watchAllocationBy,
                    ) &&
                    [
                      "SELECT_PORTFOLIO",
                      "EDIT_COMPANY",
                      "SELL_COMPANY",
                      "BUY_COMPANY",
                    ].includes(watchRightVisibleTable))
                }
              >
                <Grid2 size={12} container>
                  <Grid2 size={3.2} />
                  <Grid2
                    size={8.8}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={
                      ["EDIT_COMPANY", "SELL_COMPANY", "BUY_COMPANY"].includes(
                        watchRightVisibleTable,
                      )
                        ? "space-between"
                        : "flex-end"
                    }
                  >
                    <Show.When
                      isTrue={[
                        "EDIT_COMPANY",
                        "SELL_COMPANY",
                        "BUY_COMPANY",
                      ].includes(watchRightVisibleTable)}
                    >
                      <GKButton
                        variant="outlined"
                        color="dark"
                        onClick={() => {
                          if (watchRightVisibleTable === "BUY_COMPANY") {
                            form.setValue(
                              "extraThings.rightVisibleTable",
                              "SELL_COMPANY",
                            );
                          } else {
                            form.setValue(
                              "extraThings.rightVisibleTable",
                              "SELECT_PORTFOLIO",
                            );
                          }
                        }}
                        startIcon={<ArrowLeftIcon />}
                      >
                        {watchRightVisibleTable === "BUY_COMPANY"
                          ? "Back to Sell Company"
                          : "Back to Portfolios"}
                      </GKButton>
                    </Show.When>

                    <Show.When
                      isTrue={["SELECT_PORTFOLIO", "SELL_COMPANY"].includes(
                        watchRightVisibleTable,
                      )}
                    >
                      <GKButton
                        variant="contained"
                        onClick={() => {
                          const isRebalanceCustom =
                            watchAllocationType === "rebalance" &&
                            watchAllocationBy === "customized";

                          const isSelectPortfolio =
                            watchRightVisibleTable === "SELECT_PORTFOLIO";

                          const isSellCompany =
                            watchRightVisibleTable === "SELL_COMPANY";

                          const companies =
                            form.getValues("allocation.companies") ?? [];
                          const sellCompanies =
                            form.getValues("allocation.sellCompanies") ?? [];
                          const buyCompanies =
                            form.getValues("allocation.buyCompanies") ?? [];

                          const shouldOpenCompanyModal =
                            (!isRebalanceCustom && companies.length === 0) ||
                            (isRebalanceCustom &&
                              isSelectPortfolio &&
                              sellCompanies.length === 0) ||
                            (isRebalanceCustom &&
                              isSellCompany &&
                              buyCompanies.length === 0);

                          if (shouldOpenCompanyModal) {
                            setOpenCompanyModel(true);
                          } else {
                            if (isRebalanceCustom) {
                              form.setValue(
                                "extraThings.rightVisibleTable",
                                isSelectPortfolio
                                  ? "SELL_COMPANY"
                                  : "BUY_COMPANY",
                              );
                            } else {
                              form.setValue(
                                "extraThings.rightVisibleTable",
                                "EDIT_COMPANY",
                              );
                            }
                          }
                        }}
                        endIcon={<ArrowRightIcon />}
                      >
                        {watchAllocationType === "rebalance" &&
                        watchAllocationBy === "customized"
                          ? `Next, Select ${watchRightVisibleTable === "SELECT_PORTFOLIO" ? "Exit" : "Buy"} Companies`
                          : "Next, Select Companies"}
                      </GKButton>
                    </Show.When>

                    {["EDIT_PORTFOLIO", "EDIT_COMPANY", "BUY_COMPANY"].includes(
                      watchRightVisibleTable,
                    ) && (
                      <GKButton
                        variant={"contained"}
                        startIcon={<DocumentIcon color="var(--white)" />}
                        endIcon={<ArrowRightIcon />}
                        type="submit"
                        loading={isLoading}
                      >
                        Generate Trades
                      </GKButton>
                    )}
                  </Grid2>
                </Grid2>
              </Show.When>

              {/* ALLOCATION-TYPE = 'EXIT', ALLOCATION-BY= 'MODEL(VERSION)' */}
              <Show.When
                isTrue={
                  watchRightVisibleTable === "SELECT_PORTFOLIO" &&
                  ((watchAllocationType === "exit" &&
                    watchAllocationBy === "version") ||
                    (watchAllocationType === "rebalance" &&
                      watchAllocationBy === "company"))
                }
              >
                <Grid2 size={12} container>
                  <Grid2 size={3.2} />
                  <Grid2
                    size={8.8}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"flex-end"}
                  >
                    <GKButton
                      variant={"contained"}
                      startIcon={<DocumentIcon color="var(--white)" />}
                      endIcon={<ArrowRightIcon />}
                      type="submit"
                      loading={isLoading}
                    >
                      Generate Trades
                    </GKButton>
                  </Grid2>
                </Grid2>
              </Show.When>

              <Show.When
                isTrue={watchRightVisibleTable === "GENERATE_TRADE_TABLE"}
              >
                <Grid2
                  size={12}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                  gap={1}
                >
                  <Show.When isTrue={watchAllocationBy !== "customized"}>
                    <GKButton
                      variant={"outlined"}
                      color={"dark"}
                      startIcon={<CalculatorIcon />}
                      type="submit"
                      loading={isLoading}
                    >
                      Re-Calculate
                    </GKButton>
                  </Show.When>
                  <GKButton
                    variant={"contained"}
                    startIcon={<DocumentIcon />}
                    onClick={() => {
                      setOpenRationalModal(true);
                    }}
                  >
                    Generate Order Sheet
                  </GKButton>
                </Grid2>
              </Show.When>
            </Grid2>
          </form>

          <Show.When isTrue={openRationalModal}>
            <GenerateOrderSheetModal
              open={openRationalModal}
              setOpen={setOpenRationalModal}
              modelId={modelData.id}
              modelPortfolioList={modelPortfolioList?.data.data ?? []}
            />
          </Show.When>
        </FormProvider>
      </GKSideModal>

      <Show.When isTrue={openCompanyModel}>
        <CompanyModel
          open={openCompanyModel}
          setOpen={setOpenCompanyModel}
          modelTitle={
            watchAllocationType === "rebalance" &&
            watchAllocationBy === "customized" &&
            watchRightVisibleTable === "SELECT_PORTFOLIO"
              ? "Select Sell Companies"
              : watchAllocationType === "rebalance" &&
                  watchAllocationBy === "customized" &&
                  watchRightVisibleTable === "SELL_COMPANY"
                ? "Select Buy Companies"
                : "Select Companies"
          }
          selectedCompanies={
            watchAllocationType === "rebalance" &&
            watchAllocationBy === "customized" &&
            watchRightVisibleTable === "SELECT_PORTFOLIO"
              ? form.getValues("allocation.sellCompanies")
              : watchAllocationType === "rebalance" &&
                  watchAllocationBy === "customized" &&
                  watchRightVisibleTable === "SELL_COMPANY"
                ? form.getValues("allocation.buyCompanies")
                : form.getValues("allocation.companies")
          }
          onSuccess={(companyList) => {
            if (
              watchAllocationType === "rebalance" &&
              watchAllocationBy === "customized"
            ) {
              if (watchRightVisibleTable === "SELECT_PORTFOLIO") {
                form.setValue("allocation.sellCompanies", companyList);
                form.setValue("extraThings.rightVisibleTable", "SELL_COMPANY");
              } else {
                form.setValue("allocation.buyCompanies", companyList);
                form.setValue("extraThings.rightVisibleTable", "BUY_COMPANY");
              }
            } else {
              form.setValue("allocation.companies", companyList);
              form.setValue("extraThings.rightVisibleTable", "EDIT_COMPANY");
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default OrderSimulation;
